import type { TimeRange } from "@volca/ui/components/ui/time-range-picker";
import { dayjs } from "@/core/utils/date";
import type { ReportCalendar } from "@/profitability/calendar/types";
import type { ReportDates } from "@/profitability/types";
import { formatReportPeriod, toIsoDate } from "@/profitability/utils";

const range = (start: dayjs.Dayjs, end: dayjs.Dayjs): TimeRange => {
  const dates = {
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
  };
  return {
    from: start.toDate(),
    to: end.toDate(),
    label: formatReportPeriod(dates),
  };
};

const MONDAY_WEEKS: ReportCalendar = {
  period_type: "weekly",
  week_starts_on: 1,
  timezone: null,
};

export const PAY_PERIOD_DAYS = 14;

/**
 * The first day of the pay period holding `date`, counted from the anchor in
 * steps of 14 days, before it as well as after. The API counts the same way
 * (`ProfitabilityReportConfig#pay_period_on`).
 */
export const payPeriodStart = (anchor: string, date: dayjs.Dayjs) => {
  const start = dayjs(anchor).startOf("day");
  const days = Math.round(date.startOf("day").diff(start, "day", true));
  return start.add(Math.floor(days / PAY_PERIOD_DAYS) * PAY_PERIOD_DAYS, "day");
};

/**
 * The pay period after the last one run, or before any run the last full pay
 * period before today.
 */
const nextPayPeriod = (last: ReportDates | null, anchor: string) => {
  let start: dayjs.Dayjs;
  if (last) {
    const after = dayjs(last.end_date).add(1, "day");
    start = payPeriodStart(anchor, after);
    if (start.isBefore(after, "day")) start = start.add(PAY_PERIOD_DAYS, "day");
  } else {
    start = payPeriodStart(anchor, dayjs()).subtract(PAY_PERIOD_DAYS, "day");
  }
  return range(start, start.add(PAY_PERIOD_DAYS - 1, "day"));
};

/** The last full week before today, starting on the calendar's day. */
const lastWeek = (startsOn: number) => {
  const today = dayjs().startOf("day");
  const start = today
    .subtract((today.day() - startsOn + 7) % 7, "day")
    .subtract(7, "day");
  return range(start, start.add(6, "day"));
};

/**
 * The period a new run most likely covers, by the template's calendar: the
 * month, pay period or week after the last one (a week starting on the
 * business's own day, not always Monday; a pay period counted from the
 * calendar's anchor), or for a custom calendar the stretch after the last run,
 * as long as it was. Before any run, the last full period. Only a suggestion;
 * the owner confirms or changes it.
 */
export const proposePeriod = (
  last: ReportDates | null,
  calendar: ReportCalendar = MONDAY_WEEKS,
): TimeRange => {
  if (calendar.period_type === "biweekly" && calendar.period_anchor_date)
    return nextPayPeriod(last, calendar.period_anchor_date);
  if (calendar.period_type === "monthly") {
    const start = last
      ? dayjs(last.end_date).add(1, "day").startOf("month")
      : dayjs().subtract(1, "month").startOf("month");
    return range(start, start.endOf("month").startOf("day"));
  }
  if (!last) return lastWeek(calendar.week_starts_on);
  const after = dayjs(last.end_date).add(1, "day");
  if (calendar.period_type === "weekly") {
    const start = after.add(
      (calendar.week_starts_on - after.day() + 7) % 7,
      "day",
    );
    return range(start, start.add(6, "day"));
  }
  const days = dayjs(last.end_date).diff(dayjs(last.start_date), "day") + 1;
  return range(after, after.add(days - 1, "day"));
};

/** The period before `period` by the calendar; null for a custom one. */
const periodBefore = (
  period: ReportDates,
  calendar: ReportCalendar,
): ReportDates | null => {
  const start = dayjs(period.start_date);
  const span = (from: dayjs.Dayjs, to: dayjs.Dayjs) => ({
    start_date: from.format("YYYY-MM-DD"),
    end_date: to.format("YYYY-MM-DD"),
  });
  switch (calendar.period_type) {
    case "weekly":
      return span(start.subtract(7, "day"), start.subtract(1, "day"));
    case "biweekly":
      return span(
        start.subtract(PAY_PERIOD_DAYS, "day"),
        start.subtract(1, "day"),
      );
    case "monthly": {
      const month = start.subtract(1, "month").startOf("month");
      return span(month, month.endOf("month"));
    }
    case "custom":
      return null;
    default: {
      const unknown: never = calendar.period_type;
      return unknown;
    }
  }
};

/**
 * The periods before the suggested one that have not run, newest first:
 * back to the last period that ran, or, before any ran, a few back. The
 * first files of a template are often the weeks already past, not only the
 * next one.
 */
export const earlierPeriods = (
  next: ReportDates | null,
  last: ReportDates | null,
  ran: ReportDates[],
  calendar: ReportCalendar = MONDAY_WEEKS,
  max = 4,
): ReportDates[] => {
  const found: ReportDates[] = [];
  let period = next && periodBefore(next, calendar);
  while (period && found.length < max) {
    if (last && !dayjs(period.start_date).isAfter(last.end_date, "day")) break;
    const start = period.start_date;
    if (!ran.some((item) => item.start_date === start)) found.push(period);
    period = periodBefore(period, calendar);
  }
  return found;
};

/** A period given by its dates (a link's preset), as the picker holds it. */
export const periodRange = (dates: ReportDates): TimeRange =>
  range(dayjs(dates.start_date), dayjs(dates.end_date));

export const periodDates = (value: TimeRange | null): ReportDates | null =>
  value
    ? { start_date: toIsoDate(value.from), end_date: toIsoDate(value.to) }
    : null;

/** A period short enough for a button: "Oct 19–25", "Sep 28–Oct 4", "Feb". */
export const shortPeriod = (dates: ReportDates) => {
  const start = dayjs(dates.start_date);
  const end = dayjs(dates.end_date);
  if (start.date() === 1 && end.isSame(start.endOf("month"), "day"))
    return start.format("MMMM");
  return start.isSame(end, "month")
    ? `${start.format("MMM D")}–${end.format("D")}`
    : `${start.format("MMM D")}–${end.format("MMM D")}`;
};
